import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { newsApi, reelApi, categoryApi, getCached, setCached } from '../utils/api';
import Hero from '../components/homepage/Hero';
import FeedSection from '../components/homepage/FeedSection';
import TrendingNews from '../components/homepage/TrendingNews';
import ReelsSection from '../components/homepage/ReelsSection';
import VividhSection from '../components/homepage/VividhSection';
import SectionHeading from '../components/common/SectionHeading';
import NewsCard from '../components/common/NewsCard';
import LatestNews from '../components/common/LatestNews';
import AdBanner from '../components/common/AdBanner';

// API response hamesha array huna sakdaina (proxy fail/error object) — crash huna na deuna
const toArray = (v) => (Array.isArray(v) ? v : []);

// Categories that already have dedicated homepage sections
const FIXED_SECTIONS = ['politics', 'sports', 'economy', 'entertainment', 'tech', 'world', 'opinion'];

// Shared framer-motion reveal (scroll ma aauda fade+slide)
const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
};

function HomePage({ onOpen }) {
  const navigate = useNavigate();
  const [allNews, setAllNews] = useState([]);
  const [reels, setReels] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let mounted = true;
    // CACHE FIRST — ghrupanna turantai dekhine (pahile ko visit bata saved)
    const cached = getCached('news_home');
    const cachedList = toArray(cached?.data);
    if (cachedList.length && allNews.length === 0) {
      setAllNews(cachedList);
    }
    (async () => {
      try {
        const [n, r, c] = await Promise.all([
          newsApi.getAll({ limit: 80 }),
          reelApi.getAll(),
          categoryApi.getAll(),
        ]);
        if (!mounted) return;
        const list = toArray(n.data) || [];
        setAllNews(list);
        setCached('news_home', list);
        setReels(toArray(r.data) || []);
        setCategories(toArray(c.data) || []);
      } catch (e) {
        console.error('Error loading homepage:', e);
        if (!mounted) return;
        const fallback = getCached('news_home');
        const fb = toArray(fallback?.data);
        if (fb.length) setAllNews(fb);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Har section le afno news list afai dekhauchha — kunai article aru section bata
  // "harayera" jasto dekhinhaina (duplicate dekhina paaye pani kehi missing hudaina)
  const sections = useMemo(() => {
    // पहिले नै array बनाउने — kahi bata object/server fail aaye pani crash nahos
    const newsList = toArray(allNews);
    const catList = toArray(categories);
    const bySlug = (slug) => newsList.filter((n) => n.category_slug === slug);
    // Sabai bhanda naya thapgareko news sabai bhanda mathi (id DESC = add order)
    const latestFirst = [...newsList].sort((a, b) => b.id - a.id);
    // Sidebar ma sadhai 5 ota dekhaune — article kam bhaye pani surubata wrap garera
    const pickSide = (from, count) => {
      const out = [];
      for (let k = 0; k < count && latestFirst.length > 0; k++) {
        out.push(latestFirst[(from + k) % latestFirst.length]);
      }
      return out;
    };

    return {
      heroItems: latestFirst.slice(0, 5),
      heroSide: pickSide(5, 5),
      topNews: newsList.slice(0, 6),
      feed: {
        politics: bySlug('politics').slice(0, 9),
        sports: bySlug('sports').slice(0, 9),
        economy: bySlug('economy').slice(0, 9),
        entertainment: bySlug('entertainment').slice(0, 9),
        tech: bySlug('tech').slice(0, 9),
        world: bySlug('world').slice(0, 9),
        opinion: bySlug('opinion').slice(0, 9),
      },
      // Admin-added categories with published news get their own section below मनोरञ्जन
      extraCats: catList
        .filter((c) => c.slug && !FIXED_SECTIONS.includes(c.slug))
        .map((c) => ({ ...c, items: bySlug(c.slug).slice(0, 9) }))
        .filter((c) => c.items.length > 0),
      vividh: latestFirst.slice(6, 10),
    };
  }, [allNews, categories]);

  // Sidebar keeps duplicates by design
  const latest = allNews.slice(0, 5);

  return (
    <main className="page max-w-[1240px] mx-auto px-5 pt-7 pb-[60px] w-full">
      {/* Ad banner just below the marquee — ad chhaina bhane puro nai hide hunchha (gap pani) */}
      <AdBanner position="header" className="mb-6" />

      {/* Hero — page khulepachhi fade-in */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
      >
        <Hero items={sections.heroItems} side={sections.heroSide} onOpen={onOpen} />
      </motion.div>

      <div className="grid gap-8 lg:grid-cols-[1fr_340px] items-start">
        <div className="min-w-0">
          {/* मुख्य समाचार */}
          <motion.section
            {...fadeUp}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="mb-8"
          >
            <SectionHeading title="मुख्य समाचार" onViewAll={() => navigate('/news')} />
            <div className="grid gap-7 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full">
              {sections.topNews.map((item) => (
                <NewsCard key={item.id} item={item} onOpen={onOpen} />
              ))}
            </div>
          </motion.section>

          <FeedSection
            title="राजनीति"
            catId="politics"
            items={sections.feed.politics}
            limit={6}
            onOpen={onOpen}
            onViewAll={() => navigate('/rajniti')}
          />

          <FeedSection
            title="खेलकुद"
            catId="sports"
            items={sections.feed.sports}
            limit={6}
            onOpen={onOpen}
            onViewAll={() => navigate('/khelkud')}
          />

          <motion.div {...fadeUp} transition={{ duration: 0.45 }}>
            <AdBanner position="infeed" noFallback />
          </motion.div>

          <FeedSection
            title="अर्थतन्त्र"
            catId="economy"
            items={sections.feed.economy}
            limit={6}
            onOpen={onOpen}
            onViewAll={() => navigate('/category/economy')}
          />

          <FeedSection
            title="मनोरञ्जन"
            catId="entertainment"
            items={sections.feed.entertainment}
            limit={6}
            onOpen={onOpen}
            onViewAll={() => navigate('/category/entertainment')}
          />

          <FeedSection
            title="प्रविधि"
            catId="tech"
            items={sections.feed.tech}
            limit={6}
            onOpen={onOpen}
            onViewAll={() => navigate('/category/tech')}
          />

          <FeedSection
            title="विश्व"
            catId="world"
            items={sections.feed.world}
            limit={6}
            onOpen={onOpen}
            onViewAll={() => navigate('/category/world')}
          />

          <FeedSection
            title="विचार"
            catId="opinion"
            items={sections.feed.opinion}
            limit={6}
            onOpen={onOpen}
            onViewAll={() => navigate('/bichar')}
          />

          {/* Dynamically added categories (admin) appear here */}
          {sections.extraCats.map((c) => (
            <FeedSection
              key={c.slug}
              title={c.name}
              catId={c.slug}
              color={c.color}
              items={c.items}
              limit={6}
              onOpen={onOpen}
              onViewAll={() => navigate(`/category/${c.slug}`)}
            />
          ))}

          {/* गृहपृष्ठ प्रविधि तल — ad */}
          <motion.div {...fadeUp} transition={{ duration: 0.45 }}>
            <AdBanner position="tech_below" maxAds={1} showAll={false} className="mb-8" />
          </motion.div>
        </div>

        {/* Sidebar — scroll ma aaune bitikai reveal */}
        <motion.aside
          initial={{ opacity: 0, x: 28 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.55, ease: 'easeOut', delay: 0.1 }}
          className="flex flex-col gap-6 lg:sticky lg:top-24 min-w-0"
        >
          <LatestNews items={latest} onOpen={onOpen} />
          <TrendingNews news={allNews.slice(0, 6)} onOpen={onOpen} />
          <div className="flex flex-col gap-4">
            <AdBanner position="sidebar-top" maxAds={2} showAll={false} />
            <AdBanner position="sidebar-bottom" maxAds={2} showAll={false} />
          </div>
        </motion.aside>
      </div>

      {/* थप कथाहरू (reels) */}
      <motion.div {...fadeUp} transition={{ duration: 0.5, ease: 'easeOut' }}>
        <ReelsSection reels={reels} />
      </motion.div>

      {/* विविध समाचार */}
      <motion.div {...fadeUp} transition={{ duration: 0.5, ease: 'easeOut' }}>
        <VividhSection items={sections.vividh} onOpen={onOpen} onViewAll={() => navigate('/vividh')} />
      </motion.div>
    </main>
  );
}

export default HomePage;